/**
 * WHAT A RUNG'S PAYLOAD IS ALLOWED TO CLAIM — the honesty layer between the routes and the pane.
 *
 * ⛔ THIS EXISTS BECAUSE BUILDER-bb HANDED THE HUD THREE TRAPS IN WRITING AND SAID *"CHECK IT — DO
 * NOT TRUST ME."* Every one of them fails toward a plausible picture rather than an error:
 * ```
 *   1 o/h/l/c are ** NULL, never 0 **, when a bucket held no priced tick.
 *     ** A 0.00 ON A PRICE AXIS IS A FABRICATED PRINT. ** Render a GAP, not a candle.
 *   2 `stale` means ** NO TICKS ARE ARRIVING **, NOT "no trades are happening".
 *     A budget keyed on bucket arrival paints red all night and trains the owner
 *     to ignore the chip.
 *   3 `counts` MUST CLOSE: present + ambiguous == rungs.
 * ```
 * ⇒ ***A CONTRACT YOU WERE HANDED IS STILL A CLAIM UNTIL YOUR OWN CODE REFUSES THE SHAPE THAT
 * BREAKS IT.*** So each trap is a function here, not a note in a docblock.
 *
 * ⭐ AND THE SECOND JOB, WHICH IS A DEFECT IN MY OWN LANDED MODULE. `rungLadder.ts` declares
 * `DRIVEN_RUNGS` *"evidence, not configuration"* — but it is assigned `RUNG_LADDER`, so it cannot
 * refuse a rung and `rungOptions(DRIVEN_RUNGS)` is the whole ladder by construction. GK-zz and
 * GK-ac both flagged that no GATE verified the driving; the sharper fact is that **the structure
 * could not express a rung being undriven.** ⇒ ***A CONSTANT THAT EQUALS ITS OWN UNIVERSE IS A
 * CLAIM WEARING A GUARD'S CLOTHES.*** `servedRungs()` below derives the set from what the routes
 * ACTUALLY ANSWERED, so "served" can finally be smaller than "declared".
 */

import { rungKey, type Rung } from "./rungLadder.ts";

/** The honesty fields both shapes carry. Anything absent is `undefined`, never defaulted. */
export interface RungEnvelope {
  readonly stale?: boolean | null;
  readonly age_s?: number | null;
  /**
   * ⛔ SERVED AND PREVIOUSLY UNTYPED — added by measurement, not by assumption (HUDCHAT-10,
   * 2026-08-25). `/api/es/cvd/submin?span=5s` returns `budget_s: 90` and `capped: false` on every
   * live response, and this interface modelled neither. That is the SAME two-sides-disagree shape
   * `RungBar.bar_open` documents below, one field over: a consumer reaching for `env.budget_s`
   * got a type error for a field the route has always sent.
   * ⚠ Still OPTIONAL and NULLABLE — the minute route is a different envelope, and absence must
   * stay expressible. Modelling what IS served is the cure; asserting it is always there is not.
   */
  readonly budget_s?: number | null;
  readonly capped?: boolean | null;
  readonly empty?: boolean | null;
  readonly empty_reason?: string | null;
  readonly study_status?: string | null;
  readonly counts?: { rungs?: number; ambiguous?: number; present?: number } | null;
}

/**
 * One bucket, in the shape the sub-minute route serves it (price and flow on ONE row).
 *
 * ⛔ `bar_open` WAS TYPED `number` HERE AND THE ROUTE HAS NEVER SERVED A NUMBER. Measured against
 * the DEPLOYED worker 2026-08-24 18:39Z, `/api/es/cvd/submin?span=5s`:
 * ```
 *   bar_open   string   "2026-08-24T18:39:05.000Z"      <- ISO, and `t` was ABSENT
 *   submin.ts:59 (the worker's own decl)  `readonly bar_open: string`
 * ```
 * ⇒ ***THE TWO SIDES OF ONE FIELD DISAGREED ON ITS TYPE, IN THE SAME REPO*** — the worker said
 * string, this said number, and nothing consumed it yet so nothing ever went red. That is not a
 * dormant nit: it is the `-Infinity` defect's whole mechanism, and it is REPRODUCED on the live
 * payload — `bars.map(b => b.bar_open).filter(Number.isFinite)` empties a 2-bar set, and
 * `Math.max(...[])` is `-Infinity`. A pane that trusts the declared type draws a price axis
 * anchored at negative infinity, with no chip, from a route that answered perfectly.
 * ⚠ `t` is R649's ADDITIVE cure (bi, `f8e1948`) — epoch ms, derived server-side from this row's own
 * `bar_open`, so the two CANNOT disagree. It is typed OPTIONAL because the deployed route does not
 * serve it yet; see `barTimeMs`, which is why that absence is survivable rather than blank.
 */
export interface RungBar {
  /** Epoch ms. R649's cure. ABSENT on any route that predates bi's deploy — never assume it. */
  readonly t?: number | null;
  /** ISO-8601 with a zone designator, as the worker declares it. Numbers tolerated, not expected. */
  readonly bar_open?: string | number | null;
  readonly o?: number | null;
  readonly h?: number | null;
  readonly l?: number | null;
  readonly c?: number | null;
  readonly delta?: number | null;
  readonly n_signed?: number | null;
  readonly ambiguous?: boolean | null;
  /**
   * ⛔ THE SECOND HALF OF THE SAME NARROWNESS THAT HID THE `bar_open` TYPE LIE. This interface
   * declared nine fields; the DEPLOYED route serves fourteen, measured field-for-field at the
   * worker venue 2026-08-24 18:39Z. `cvd` is the one that matters — it is the CUMULATIVE the lane
   * actually draws, and it was simply absent from the type, so any consumer reaching for it got a
   * compile error rather than data and would have been tempted to cast around it.
   * ⇒ ***A TYPE NARROWER THAN THE WIRE IS NOT "SAFE", IT IS A SECOND, QUIETER CONTRACT*** — the
   * first one told the pane `bar_open` was a number; this one told it `cvd` did not exist.
   * ⚠ Every field below was OBSERVED on the live payload, not inferred from the SQL.
   */
  readonly cvd?: number | null;
  readonly cvd_o?: number | null;
  readonly cvd_h?: number | null;
  readonly cvd_l?: number | null;
  readonly n_unsigned?: number | null;
  readonly n_ticks?: number | null;
  readonly n_priced?: number | null;
  /** Minute rows name `instrument`; sub-minute rows historically name `instrument_id`. */
  readonly instrument?: string | null;
  readonly instrument_count?: number | null;
  readonly instrument_id?: string | null;
  /**
   * ⛔⛔ ORDER 77 — AND THIS INTERFACE'S OWN LAW, TWO FIELDS LATER, ON ME.
   *
   * The docblock above records that this type was once NARROWER THAN THE WIRE and calls that "a
   * second, quieter contract". ORDER 77 added exactly these two fields to the served payload and
   * they were not added here — so `subminCvdFeed` enumerated the bar it knew about, the keys fell
   * on the floor between the worker and the pane, and the 5s/15s/30s rungs logged NO SESSION
   * ANCHOR over correctly anchored values (R1403, GK-br confirmed at the line).
   * ⇒ ***THE SAME DEFECT THE COMMENT ABOVE WAS WRITTEN TO PREVENT, IN THE SAME INTERFACE.*** A law
   *   recorded beside a field does not protect the field added under it.
   * ⚠ AND WHY MY WORKER-SIDE CURE COULD NOT CATCH THIS: I made these REQUIRED on `SubminBucket` so
   *   a dropped mapper became a compile error. That worked — in `workers/read-api`. This is a
   *   different package with its own type, and the compiler never compared the two. A type is only
   *   a guard where one compiler sees BOTH ends of the hop.
   */
  readonly session_key?: string | null;
  readonly session_cvd_before?: number | null;
}

/**
 * ⛔ TRAP 1 — A BUCKET WITH NO PRICED TICK IS A GAP, NOT A ZERO.
 *
 * `o/h/l/c` arrive NULL. Anything that coerces them (`?? 0`, `Number(x)`, `+x`) paints a candle at
 * 0.00 on a price axis, which is not a missing print — it is an INVENTED one, and it rescales the
 * whole pane around it. ⚠ `0` is returned as PRESENT deliberately: a real tick can print 0 on some
 * instruments, and conflating "absent" with "zero" is the defect, in both directions.
 */
export function barIsGap(b: RungBar): boolean {
  return b.o == null || b.h == null || b.l == null || b.c == null;
}

/** Only the bars that may be drawn as candles. Gaps are LEFT OUT, never zero-filled. */
export function drawableBars(bars: readonly RungBar[]): RungBar[] {
  return bars.filter((b) => !barIsGap(b));
}

/**
 * ⛔ TRAP 3 — `counts` MUST CLOSE, AND bb SAID TO CHECK IT RATHER THAN TRUST HIM.
 *
 * `present + ambiguous === rungs`. A payload whose own census does not add up is not a payload
 * with a small error — it is a payload that cannot say what it contains, and a pane that draws it
 * anyway is asserting a completeness nobody measured.
 * ⚠ Returns `null` when `counts` is ABSENT: that is "no claim made", which must not read as
 * "claim verified". `api.ts` rules the same for `tf_applied` — *absent means NO MISMATCH CLAIM.*
 */
export function countsClose(env: RungEnvelope): boolean | null {
  const c = env.counts;
  if (c == null) return null;
  const { rungs, ambiguous, present } = c;
  if (typeof rungs !== "number" || typeof ambiguous !== "number" || typeof present !== "number") {
    return null;
  }
  return present + ambiguous === rungs;
}

/**
 * ⛔ TRAP 2 — `stale` IS ABOUT TICK ARRIVAL, NOT ABOUT TRADING.
 *
 * Overnight and at the weekend a healthy feed legitimately delivers nothing. A chip keyed on
 * "bucket arrived recently" is red all night, and ***A CHIP THAT IS RED WHEN NOTHING IS WRONG
 * TRAINS THE OWNER TO IGNORE IT*** — which costs exactly the one night it is right.
 *
 * So the verdict is named rather than boolean, and NO-TICKS is deliberately distinct from STALE:
 *   FRESH      ticks arriving, inside budget
 *   NO-TICKS   the route says stale — say so WITHOUT claiming a fault
 *   UNKNOWN    the route made no claim (absent field) — never guessed at
 */
export type FreshVerdict = "FRESH" | "NO-TICKS" | "UNKNOWN";

export function freshness(env: RungEnvelope): FreshVerdict {
  if (env.stale == null) return "UNKNOWN";
  return env.stale ? "NO-TICKS" : "FRESH";
}

/**
 * MONEY-FLOW PRESS, ratified client-side by R566(3): `press = delta / n_signed`.
 *
 * ⛔ `n_signed === 0` YIELDS `null`, NEVER `0`. DESK-n20 R6(5): ***a zero-flow bar is ABSENCE, NOT
 * NEUTRALITY.*** Returning 0 would draw a bar sitting exactly on the midline — a confident claim of
 * balanced flow, made from no signed ticks at all.
 * ⚠ My own live sample (5s/15s/30s at the worker venue) contained ZERO such buckets, because the
 * session was active. bb measured 68% of 5s buckets overnight and 12.5% holding ONE tick. ⇒ ***A
 * SAMPLE THAT NEVER CONTAINS THE FAILING INPUT IS NOT EVIDENCE THE FORMULA IS SAFE*** — which is
 * precisely why this guard ships in the same object as the formula.
 */
export function press(b: RungBar): number | null {
  const d = b.delta, n = b.n_signed;
  if (d == null || n == null || n === 0) return null;
  return d / n;
}

/** A rung's probe result: the envelope it answered with, or `null` if it did not answer. */
export type RungProbe = { readonly rung: Rung; readonly env: RungEnvelope | null };

/**
 * THE RUNGS THE HUB HAS ACTUALLY SEEN ANSWER — the cure for a constant that gated nothing.
 *
 * A rung is SERVED iff it answered with an envelope that makes a claim this hub can read. An
 * `empty:true` envelope still counts as SERVED: the route answered, and *"EMPTY says nothing about
 * whether ticks arrived"* (cvdLane's own register). Refusing it would hide a working rung during a
 * quiet session — the same defect as a stale-chip keyed on arrival.
 *
 * ⛔ WHAT IS *NOT* SERVED: a rung with no envelope at all. That is the route not answering, and it
 * must not be offered — ***A CONTROL IS ONLY AS HONEST AS THE WORST RUNG IT CAN REACH.***
 * ⚠ AND A PAYLOAD WHOSE `counts` DO NOT CLOSE IS NOT SERVED EITHER. It answered, but it cannot say
 * what it contains, and offering it would let the owner select a rung whose completeness is
 * unmeasured. `null` (no claim) is tolerated; `false` (a claim that failed) is not.
 */
export function servedRungs(probes: readonly RungProbe[]): Rung[] {
  return probes
    .filter((p) => p.env != null && countsClose(p.env) !== false)
    .map((p) => p.rung);
}

/** Convenience for the pane: did every probe fail? Distinct from "some rungs are quiet". */
export function allRungsUnreachable(probes: readonly RungProbe[]): boolean {
  return probes.length > 0 && probes.every((p) => p.env == null);
}

/** Stable, sorted identity for a probe set — so a pane can tell "changed" from "re-rendered". */
export function servedKey(probes: readonly RungProbe[]): string {
  return servedRungs(probes).map(rungKey).join(",");
}

// -------------------------------------------------------------------------------------------------
// TRAP 4 — WHAT INSTANT A BUCKET NAMES. R649 / R650's S4b "bind `t` ... never -Infinity".
// -------------------------------------------------------------------------------------------------

/**
 * ⛔ A ZONE-LESS TIMESTAMP IS A DIFFERENT INSTANT ON EVERY MACHINE.
 *
 * `Date.parse("2026-08-24T18:39:05")` — no `Z`, no offset — is read as LOCAL time, so the same
 * payload yields a different number in London, Kolkata and Chatham. It does not fail; it SHIFTS,
 * silently, by the host's offset. This estate has already been bitten by a local-time reading
 * pasted into a UTC context, and a chart is the worst place to land one: every bar moves together,
 * so the picture looks entirely correct and is simply in the wrong hour.
 *
 * ⭐ THIS IS A POSITIVE PREDICATE, NOT A BLOCKLIST OF BAD SPELLINGS: the string must SAY which
 * zone it means. Anything that does not is refused, whatever it looks like. The arm proves the
 * property behaviourally — the same input is parsed under five `TZ` values and must agree.
 */
const ZONED = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * ⛔ A FINITE NUMBER IS NOT A TIMESTAMP. `codex review` [P2], and it is right: epoch SECONDS are
 * finite, so a route regressing from ms to s passes every `Number.isFinite` check and draws the
 * whole session in **January 1970** — a plausible picture, silently, which is strictly worse than a
 * refusal. `1787596745` (seconds) reads as `1970-01-21T16:33:16Z`.
 *
 * ⭐ THE PREDICATE IS ON THE VALUE'S MAGNITUDE, WHICH IS WHAT ACTUALLY SEPARATES THE UNITS — not on
 * a field name or a spelling. Seconds for any modern instant are ~1.7e9; milliseconds are ~1.7e12.
 * ⚠ BOUND, STATED: this refuses instants before 2001-09-09 and after 2286-11-20. ES sub-minute
 * buckets exist only from 2026, so no real bar is excluded; a backfill reaching further would need
 * this widened DELIBERATELY rather than by accident, which is the point.
 */
const MS_FLOOR = 1e12;
const MS_CEIL = 1e13;
const plausibleMs = (n: number): boolean =>
  Number.isFinite(n) && n >= MS_FLOOR && n < MS_CEIL;

/** Which field actually carried this bar's instant. `"none"` is a REFUSAL, never a default. */
export type BarTimeSource = "t" | "bar_open" | "none";

/**
 * THE ONE READER OF A BUCKET'S INSTANT — prefers `t`, survives its absence, and NEVER returns NaN.
 *
 * ⛔ `null` IS THE ONLY FAILURE VALUE. Returning `NaN` would be the defect restored one level down:
 * `NaN` is a number, so it passes a `typeof` check, survives into `Math.min/max`, and poisons every
 * comparison silently (`NaN !== NaN` is exactly the crossing test that fabricates a flip elsewhere
 * in this app). A caller can forget to check `NaN`; it cannot accidentally use `null` as a time.
 */
export function barTimeMs(b: RungBar): number | null {
  // R649's field first: server-derived from this row's own `bar_open`, so it cannot disagree.
  // ⚠ A BAD `t` FALLS THROUGH, IT DOES NOT FALL OVER. If a route regresses to epoch seconds, its
  // `bar_open` is still a good ISO string — refusing the bar outright would discard a readable
  // instant to punish a field. The fallback then reports source `"bar_open"`, so it is not silent.
  if (typeof b.t === "number" && plausibleMs(b.t)) return b.t;
  const raw = b.bar_open;
  if (typeof raw === "number") return plausibleMs(raw) ? raw : null;
  if (typeof raw === "string" && ZONED.test(raw)) {
    const ms = Date.parse(raw);
    return plausibleMs(ms) ? ms : null;
  }
  return null;
}

/** What `barTimeMs` read, for a pane that must SAY where its clock came from. */
export function barTimeSource(b: RungBar): BarTimeSource {
  // ⛔ THE SAME PREDICATE `barTimeMs` USES, DELIBERATELY. This read `Number.isFinite(b.t)` while
  // `barTimeMs` read `plausibleMs(b.t)`, and an epoch-SECONDS `t` then reported source `"t"` from a
  // bar whose time had actually come from `bar_open`. ⇒ ***TWO READERS OF ONE FIELD WITH TWO
  // POLICIES DIVERGE ONLY IN THE FAILURE MODES, WHICH IS WHERE NOBODY LOOKS*** — GK-ae's JOIN-1 on
  // the GEX panel is the same defect on the same day. ONE reader, or the chip lies about itself.
  if (typeof b.t === "number" && plausibleMs(b.t)) return "t";
  return barTimeMs(b) == null ? "none" : "bar_open";
}

/** How a whole payload resolved its instants — the chip's evidence. */
export interface TimeBinding {
  /** `"t"` iff EVERY timed bar carried `t`; `"bar_open"` if any fell back; `"none"` if none timed. */
  readonly source: BarTimeSource;
  readonly resolved: number;
  readonly total: number;
  /** No bar carried `t` ⇒ this route predates R649's deploy. The pane must SAY so, loudly. */
  readonly preCure: boolean;
  /** Bars the route served that name no readable instant. Nonzero is a LOUD condition. */
  readonly unreadable: number;
}

/**
 * ⛔ THE CHIP'S WHOLE JOB IS TO MAKE A DEGRADED CLOCK VISIBLE RATHER THAN SURVIVABLE.
 *
 * BUILDER-bi handed this seat the choice in writing (18:32Z): wait for the `t` deploy, or map
 * `bar_open` client-side — *"either is fine; silence is not."* Silence is what this refuses. A
 * fallback that works and says nothing is the worse of the two failures, because it removes the
 * pressure to ever land the deploy and leaves the pane's timing provenance unknowable from the UI.
 */
export function timeBinding(bars: readonly RungBar[]): TimeBinding {
  let withT = 0, resolved = 0;
  for (const b of bars) {
    const src = barTimeSource(b);
    if (src === "t") withT++;
    if (src !== "none") resolved++;
  }
  return {
    source: resolved === 0 ? "none" : withT === resolved ? "t" : "bar_open",
    resolved,
    total: bars.length,
    preCure: withT === 0,
    unreadable: bars.length - resolved,
  };
}

/**
 * THE DIRECT CURE FOR `-Infinity`: an extent that is `null` when there is nothing to extend over.
 *
 * ⛔ `Math.max(...[])` IS `-Infinity` AND `Math.min(...[])` IS `+Infinity` — a range that is not
 * merely wrong but INVERTED, and both ends are finite-looking to anything that only checks
 * `typeof`. Every caller that would have spread an array must come through here instead.
 */
export function barTimeExtent(
  bars: readonly RungBar[],
): { readonly min: number; readonly max: number } | null {
  let min = Infinity, max = -Infinity, seen = 0;
  for (const b of bars) {
    const ms = barTimeMs(b);
    if (ms == null) continue;
    seen++;
    if (ms < min) min = ms;
    if (ms > max) max = ms;
  }
  return seen === 0 ? null : { min, max };
}
